import React from 'react';
import { Form } from 'antd';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface CustomerShippingFeeFieldProps {
  /** Only a BY_CUSTOMER row charges the customer a shipping fee. */
  visible: boolean;
}

/**
 * BOXY-414 — the bulk-import row's `fee_customer_payable`, editable. Before
 * this the modal never showed or sent it, so a product price typed into the
 * sheet's fee column could not be corrected from the preview.
 */
const CustomerShippingFeeField: React.FC<CustomerShippingFeeFieldProps> = ({ visible }) => {
  const { t } = useTranslation();

  return visible ? (
    <div className='rounded-xl border border-solid border-sd-border bg-white p-4'>
      <div className='flex items-center gap-1.5 mb-2'>
        <Label
          htmlFor='row-customer-shipping-fee'
          className='text-sm font-semibold text-sd-foreground'
        >
          {String(t('customer_shipping_fee_optional'))}
        </Label>
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type='button'
                aria-label={String(t('customer_shipping_fee_tooltip'))}
                className='inline-flex border-0 bg-transparent p-0 cursor-help text-sd-muted-foreground'
              >
                <Info className='h-3.5 w-3.5' strokeWidth={2} />
              </button>
            </TooltipTrigger>
            <TooltipContent className='max-w-[260px] text-xs'>
              {String(t('customer_shipping_fee_tooltip'))}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <Form.Item name='customShipmentFee' noStyle>
        <Input
          id='row-customer-shipping-fee'
          type='number'
          inputMode='numeric'
          min={0}
          placeholder={String(t('amount'))}
          aria-describedby='row-customer-shipping-fee-help'
        />
      </Form.Item>
      <p id='row-customer-shipping-fee-help' className='text-xs text-sd-muted-foreground mt-2 mb-0'>
        {String(t('customer_shipping_fee_help'))}
      </p>
    </div>
  ) : null;
};

export default CustomerShippingFeeField;
